import * as React from "react";
import { Box, Typography, useTheme, MenuItem, TextField } from "@mui/material";
import { useSelector } from "react-redux";
import { useState } from "react";
import moment from "moment";

import StoreIcon         from "@mui/icons-material/Store";
import FilterListIcon    from "@mui/icons-material/FilterList";
import TrendingUpIcon    from "@mui/icons-material/TrendingUp";
import PeopleIcon        from "@mui/icons-material/People";
import EmojiEventsIcon   from "@mui/icons-material/EmojiEvents";

import LineChart  from "components/Graphs/LineChart";
import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import { useSalesDashboard }  from "api/hooks/dashboards";
import { useYearsList }       from "api/hooks/inventory";
import { currencyFormatter }  from "utilities/currencyFormatter";

/* ── Small section heading ── */
const SectionHeading = ({ icon, title, subtitle, accent, textPri, textSec }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "1rem" }}>
    <Box sx={{
      width: "32px", height: "32px", borderRadius: "8px",
      backgroundColor: "rgba(29,158,117,0.12)",
      display: "flex", alignItems: "center", justifyContent: "center",
      color: accent, flexShrink: 0,
    }}>
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontSize: "14px", fontWeight: 500, color: textPri, lineHeight: 1.2 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: "11px", color: textSec, mt: "1px" }}>{subtitle}</Typography>
      )}
    </Box>
  </Box>
);

/* ── Stat card ── */
const StatCard = ({ label, value, accent, textPri, textSec, cardBg, border }) => (
  <Box sx={{
    flex: "1 1 160px",
    backgroundColor: cardBg,
    border: `1px solid ${border}`,
    borderRadius: "12px",
    p: "1rem 1.25rem",
    display: "flex", flexDirection: "column", gap: "6px",
  }}>
    <Typography sx={{ fontSize: "11px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: textSec }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "20px", fontWeight: 500, color: textPri, lineHeight: 1.2 }}>
      {value}
    </Typography>
    <Box sx={{ height: "2px", width: "24px", borderRadius: "2px", backgroundColor: accent, mt: "2px" }} />
  </Box>
);

/* ── Period selector ── */
const PERIODS = ["DAY", "WEEK", "MONTH", "QUARTER", "YEAR"];

const inputSx = (cardBg, border) => ({
  "& .MuiInputBase-root": { fontSize: "13px", height: "36px", backgroundColor: cardBg, borderRadius: "8px" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
  "& .MuiInputLabel-root": { fontSize: "12px" },
});

export default function SalesDashboard() {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const { salesDashboard, setPeriodType, setPeriod } = useSalesDashboard(business_branch_id);
  const { yearList } = useYearsList();

  const [periodSelect, setPeriodSelect] = useState("DAY");
  const [quarterYear, setQuarterYear]   = useState(moment().format("YYYY"));

  const handlePeriodTypeSelect = (e) => {
    setPeriodSelect(e.target.value);
    setPeriodType(e.target.value);
  };

  /* data */
  const totalNumberOfSales   = salesDashboard?.invoice_count ?? 0;
  const invoiceAmount        = salesDashboard?.invoice_aggregation?.invoice_amount    ?? 0;
  const highestInvoice       = salesDashboard?.invoice_aggregation?.max_invoice       ?? 0;
  const averageInvoiceSale   = salesDashboard?.invoice_aggregation?.average_invoice   ?? 0;
  const recieptAmount        = salesDashboard?.reciept_aggregation?.reciept_amount     ?? 0;
  const salesInYear          = salesDashboard?.year_sales_record  ?? [];
  const salesList            = salesDashboard?.top_sales          ?? [];
  const salesPeopleList      = salesDashboard?.top_sales_people   ?? [];
  const customersList        = salesDashboard?.top_customers      ?? [];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {/* ── Page header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{
            width: "40px", height: "40px", borderRadius: "10px",
            backgroundColor: "rgba(29,158,117,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: accent, flexShrink: 0,
          }}>
            <StoreIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>
              Sales Dashboard
            </Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>
              {moment().format("dddd, D MMMM YYYY")}
            </Typography>
          </Box>
        </Box>

        {/* ── Filter bar ── */}
        <Box sx={{
          display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.75rem",
          p: "0.75rem 1rem",
          backgroundColor: cardBg,
          border: `1px solid ${border}`,
          borderRadius: "10px",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", color: accent }}>
            <FilterListIcon sx={{ fontSize: "16px" }} />
            <Typography sx={{ fontSize: "11px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.07em" }}>
              Period
            </Typography>
          </Box>

          {/* Period type pills */}
          <Box sx={{ display: "flex", gap: "5px" }}>
            {PERIODS.map((p) => (
              <Box
                key={p}
                onClick={() => handlePeriodTypeSelect({ target: { value: p } })}
                sx={{
                  px: "0.65rem", py: "0.25rem", borderRadius: "20px",
                  fontSize: "12px", fontWeight: 500, cursor: "pointer",
                  border: `1px solid ${periodSelect === p ? accent : border}`,
                  color: periodSelect === p ? accent : textSec,
                  backgroundColor: periodSelect === p ? "rgba(29,158,117,0.1)" : "transparent",
                  transition: "all 0.15s",
                  "&:hover": { borderColor: accent, color: accent },
                }}
              >
                {p.charAt(0) + p.slice(1).toLowerCase()}
              </Box>
            ))}
          </Box>

          {/* Dynamic period input */}
          {periodSelect === "DAY" && (
            <TextField label="Date" size="small" type="date"
              defaultValue={moment().format("YYYY-MM-DD")}
              onChange={(e) => setPeriod(e.target.value)}
              sx={{ ...inputSx(cardBg, border), width: "150px" }} />
          )}
          {periodSelect === "WEEK" && (
            <TextField label="Week" size="small" type="week"
              onChange={(e) => setPeriod(e.target.value)}
              sx={{ ...inputSx(cardBg, border), width: "150px" }} />
          )}
          {periodSelect === "MONTH" && (
            <TextField label="Month" size="small" type="month"
              defaultValue={moment().format("YYYY-MM")}
              onChange={(e) => setPeriod(e.target.value)}
              sx={{ ...inputSx(cardBg, border), width: "150px" }} />
          )}
          {periodSelect === "YEAR" && (
            <TextField label="Year" size="small" select
              onChange={(e) => setPeriod(e.target.value)}
              sx={{ ...inputSx(cardBg, border), width: "110px" }}>
              {yearList.map((y) => (
                <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>
              ))}
            </TextField>
          )}
          {periodSelect === "QUARTER" && (
            <>
              <TextField label="Quarter" size="small" select
                onChange={(e) => setPeriod(`${quarterYear}-${e.target.value}`)}
                sx={{ ...inputSx(cardBg, border), width: "100px" }}>
                {["Q1","Q2","Q3","Q4"].map((q) => (
                  <MenuItem key={q} value={q}>{q}</MenuItem>
                ))}
              </TextField>
              <TextField label="Year" size="small" select
                defaultValue={moment().format("YYYY")}
                onChange={(e) => setQuarterYear(e.target.value)}
                sx={{ ...inputSx(cardBg, border), width: "100px" }}>
                {yearList.map((y) => (
                  <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>
                ))}
              </TextField>
            </>
          )}
        </Box>
      </Box>

      {/* ── Stat cards ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
        {[
          { label: "Orders Made",          value: totalNumberOfSales },
          { label: "Total Amount Billed",  value: currencyFormatter(parseFloat(invoiceAmount)) },
          { label: "Total Cash Received",  value: currencyFormatter(parseFloat(recieptAmount)) },
          { label: "Highest Recorded Bill",value: currencyFormatter(parseFloat(highestInvoice)) },
          { label: "Average Bill",         value: currencyFormatter(parseFloat(averageInvoiceSale)) },
        ].map(({ label, value }) => (
          <StatCard
            key={label} label={label} value={value}
            accent={accent} textPri={textPri} textSec={textSec}
            cardBg={cardBg} border={border}
          />
        ))}
      </Box>

      {/* ── Monthly sales chart ── */}
      <Box sx={{
        backgroundColor: cardBg,
        border: `1px solid ${border}`,
        borderRadius: "12px",
        p: "1.25rem 1.5rem",
      }}>
        <SectionHeading
          icon={<TrendingUpIcon sx={{ fontSize: "18px" }} />}
          title="Monthly Sales"
          subtitle="Year-to-date performance"
          accent={accent} textPri={textPri} textSec={textSec}
        />
        <LineChart
          label={["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]}
          data={[{
            label: "Sales",
            data: salesInYear,
            fill: false,
            borderColor: accent,
            backgroundColor: "rgba(29,158,117,0.08)",
            tension: 0.3,
          }]}
        />
      </Box>

      {/* ── Bottom tables ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>

        {/* Top sales orders */}
        <Box sx={{
          flex: "1 1 300px",
          backgroundColor: cardBg,
          border: `1px solid ${border}`,
          borderRadius: "12px",
          p: "1.25rem 1.25rem 0.75rem",
          overflow: "hidden",
        }}>
          <SectionHeading
            icon={<TrendingUpIcon sx={{ fontSize: "18px" }} />}
            title="Top Sales Orders"
            subtitle={`${salesList.length} orders`}
            accent={accent} textPri={textPri} textSec={textSec}
          />
          <ReportTable
            columns={[
              { label: "Invoice #" },
              { label: "Sales Person" },
              { label: "Amount", align: "right" },
            ]}
            rows={salesList}
            emptyMessage="No sales orders found"
            renderRow={(row, index) => (
              <RRow key={row.id ?? index} index={index}>
                <RCell>{row.invoice_number}</RCell>
                <RCell>{row.sales_person}</RCell>
                <RCell align="right">{currencyFormatter(parseFloat(row.total_amount))}</RCell>
              </RRow>
            )}
          />
        </Box>

        {/* Top sales people */}
        <Box sx={{
          flex: "1 1 300px",
          backgroundColor: cardBg,
          border: `1px solid ${border}`,
          borderRadius: "12px",
          p: "1.25rem 1.25rem 0.75rem",
          overflow: "hidden",
        }}>
          <SectionHeading
            icon={<PeopleIcon sx={{ fontSize: "18px" }} />}
            title="Top Sales People"
            subtitle={`${salesPeopleList.length} staff`}
            accent={accent} textPri={textPri} textSec={textSec}
          />
          <ReportTable
            columns={[
              { label: "Employee" },
              { label: "Orders", align: "center" },
              { label: "Amount", align: "right" },
            ]}
            rows={salesPeopleList}
            emptyMessage="No sales people data found"
            renderRow={(row, index) => (
              <RRow key={row.id ?? index} index={index}>
                <RCell>{row.first_name} {row.last_name}</RCell>
                <RCell align="center">{row.invoices_created}</RCell>
                <RCell align="right">{currencyFormatter(parseFloat(row.total_invoice_amount))}</RCell>
              </RRow>
            )}
          />
        </Box>
      </Box>

      {/* ── Top customers ── */}
      <Box sx={{
        backgroundColor: cardBg,
        border: `1px solid ${border}`,
        borderRadius: "12px",
        p: "1.25rem 1.25rem 0.75rem",
        overflow: "hidden",
      }}>
        <SectionHeading
          icon={<EmojiEventsIcon sx={{ fontSize: "18px" }} />}
          title="Top Customers"
          subtitle={`${customersList.length} customers`}
          accent={accent} textPri={textPri} textSec={textSec}
        />
        <ReportTable
          columns={[
            { label: "Customer" },
            { label: "Orders", align: "center" },
            { label: "Amount", align: "right" },
          ]}
          rows={customersList}
          emptyMessage="No customer data found"
          renderRow={(row, index) => (
            <RRow key={row.id ?? index} index={index}>
              <RCell>{row.customer_name}</RCell>
              <RCell align="center">{row.invoices_created}</RCell>
              <RCell align="right">{currencyFormatter(parseFloat(row.total_invoice_amount))}</RCell>
            </RRow>
          )}
        />
      </Box>

    </Box>
  );
}